import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';
import { useAuthStore, getUserDisplayName } from './authStore';

/**
 * Session Store
 *
 * Manages user session state including role (DM vs Player).
 * Bridges identity from authStore (Supabase Auth) so all data
 * operations use the same user ID.
 */

export type UserRole = 'dm' | 'player';

interface SessionState {
  // User info
  userId: string | null;
  userName: string | null;
  role: UserRole;
  isInitialized: boolean;

  // Actions
  setRole: (role: UserRole) => void;
  setUser: (userId: string, userName: string) => void;
  toggleRole: () => void;
  initializeUser: (displayName?: string) => Promise<string>;
  syncUserToSupabase: () => Promise<void>;
  updateUserName: (newName: string) => Promise<boolean>;
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      userId: null,
      userName: null,
      role: 'dm', // Default to DM for development
      isInitialized: false,

      setRole: (role) => set({ role }),

      setUser: (userId, userName) => set({ userId, userName }),

      toggleRole: () =>
        set((state) => ({
          role: state.role === 'dm' ? 'player' : 'dm',
        })),

      initializeUser: async (displayName?: string) => {
        // Always bridge from authStore — this is the source of truth
        const authUser = useAuthStore.getState().user;
        if (authUser) {
          const name = displayName || getUserDisplayName(authUser);
          const currentUserId = get().userId;

          // If sessionStore had a DIFFERENT userId (pre-auth legacy), overwrite it
          if (currentUserId && currentUserId !== authUser.id) {
            console.warn(`[SessionStore] Replacing legacy userId ${currentUserId.slice(0, 8)}… with auth userId ${authUser.id.slice(0, 8)}…`);
          }

          set({ userId: authUser.id, userName: name, isInitialized: true });

          // Ensure machina_users row exists (authStore.syncUserToDatabase may have already done this)
          await supabase
            .from('machina_users')
            .upsert({ id: authUser.id, display_name: name }, { onConflict: 'id' });

          return authUser.id;
        }

        // Fallback: use persisted userId if auth isn't ready yet
        const { userId, userName } = get();
        if (userId) {
          set({ isInitialized: true });
          return userId;
        }

        // Last resort: create anonymous user (shouldn't happen behind AuthGuard)
        console.warn('[SessionStore] No auth user found — creating anonymous session');
        const newName = displayName || userName || `Player ${Math.floor(Math.random() * 10000)}`;
        const { data, error } = await supabase
          .from('machina_users')
          .insert({ display_name: newName })
          .select()
          .single();

        if (error) {
          console.error('Failed to create user:', error);
          const localId = `local-${Date.now()}`;
          set({ userId: localId, userName: newName, isInitialized: true });
          return localId;
        }

        set({
          userId: data.id,
          userName: data.display_name,
          isInitialized: true,
        });

        return data.id;
      },

      syncUserToSupabase: async () => {
        const { userId, userName } = get();
        if (!userId || userId.startsWith('local-')) return;

        try {
          const { data: existing, error } = await supabase
            .from('machina_users')
            .select('id, display_name')
            .eq('id', userId)
            .maybeSingle();

          if (error) {
            console.error('[SessionStore] Failed to fetch user:', error);
            return;
          }

          if (!existing) {
            console.log('[SessionStore] Creating user in Supabase:', userId, userName);
            await supabase
              .from('machina_users')
              .insert({ id: userId, display_name: userName });
          } else if (existing.display_name && existing.display_name !== userName) {
            console.log('[SessionStore] Syncing display name from DB:', existing.display_name, '(was:', userName, ')');
            set({ userName: existing.display_name });
          }
        } catch (err) {
          console.error('[SessionStore] Sync error:', err);
        }
      },

      updateUserName: async (newName: string) => {
        const { userId } = get();
        if (!newName.trim()) return false;

        const trimmedName = newName.trim();

        // Update local state immediately
        set({ userName: trimmedName });

        // If we have a Supabase user, update there too
        if (userId && !userId.startsWith('local-')) {
          try {
            const { error } = await supabase
              .from('machina_users')
              .update({ display_name: trimmedName })
              .eq('id', userId);

            if (error) {
              console.error('[SessionStore] Failed to update user name:', error);
              return false;
            }
            console.log('[SessionStore] Updated user name to:', trimmedName);
          } catch (err) {
            console.error('[SessionStore] Update name error:', err);
            return false;
          }
        }

        return true;
      },
    }),
    {
      name: 'machina-session',
    }
  )
);
